import { Button, DatePicker, Input, Select, Space, Switch, Table, Tag, Typography } from 'antd';
import { ExclamationCircleOutlined, PaperClipOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IncidentSeverityTag, IncidentStatusTag } from './StatusTag';
import type { CurrentUser, Incident, IncidentAttachment, IncidentStatus, IncidentTask, UserRole } from '../types/incident';

const { Text } = Typography;
const statusOptions: IncidentStatus[] = ['DRAFT', 'OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];
const adminRoles: UserRole[] = ['ORG_ADMIN', 'SYSTEM_ADMIN'];
type FilterControl = 'keyword' | 'status' | 'severity' | 'product' | 'createdRange' | 'roleOnly' | 'onHold' | 'attachments';
const defaultFilterControls: FilterControl[] = [
  'keyword',
  'status',
  'severity',
  'product',
  'createdRange',
  'roleOnly',
  'onHold',
  'attachments',
];

interface Props {
  incidents: Incident[];
  attachmentsByIncident: Record<string, IncidentAttachment[]>;
  tasksByIncident: Record<string, IncidentTask[]>;
  currentUser: CurrentUser | null;
  fixedContextLabel?: string;
  filterControls?: FilterControl[];
  persistFiltersInUrl?: boolean;
  showFilters?: boolean;
  showRoleFilters?: boolean;
  serverKeywordFiltering?: boolean;
  loading?: boolean;
}

const IncidentListTable = ({
  incidents,
  attachmentsByIncident,
  tasksByIncident,
  currentUser,
  fixedContextLabel,
  filterControls = defaultFilterControls,
  persistFiltersInUrl = false,
  showFilters = true,
  showRoleFilters = true,
  serverKeywordFiltering = false,
  loading = false,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const roles = useMemo(
    () => currentUser?.assignments.map((assignment) => assignment.role) || [],
    [currentUser?.assignments]
  );
  const visibleFilterControls = useMemo(() => new Set(filterControls), [filterControls]);
  const [keyword, setKeyword] = useState(() => (persistFiltersInUrl ? searchParams.get('q') || '' : ''));
  const [status, setStatus] = useState<string | undefined>(() => (persistFiltersInUrl ? searchParams.get('status') || undefined : undefined));
  const [severity, setSeverity] = useState<string | undefined>(() => (persistFiltersInUrl ? searchParams.get('severity') || undefined : undefined));
  const [productKeyword, setProductKeyword] = useState(() => (persistFiltersInUrl ? searchParams.get('product') || '' : ''));
  const [createdRange, setCreatedRange] = useState<[Dayjs | null, Dayjs | null] | null>(() => {
    if (!persistFiltersInUrl) return null;
    const createdFrom = searchParams.get('created_from');
    const createdTo = searchParams.get('created_to');
    return createdFrom || createdTo ? [createdFrom ? dayjs(createdFrom) : null, createdTo ? dayjs(createdTo) : null] : null;
  });
  const [roleOnly, setRoleOnly] = useState(() => persistFiltersInUrl && searchParams.get('role_only') === '1');
  const [pendingOnly, setPendingOnly] = useState(() => persistFiltersInUrl && searchParams.get('on_hold') === '1');
  const [hasAttachmentsOnly, setHasAttachmentsOnly] = useState(() => persistFiltersInUrl && searchParams.get('has_attachments') === '1');

  const setFilterParam = (key: string, value?: string | null) => {
    if (!persistFiltersInUrl) return;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      return next;
    }, { replace: true });
  };

  const setFilterParams = (entries: Array<[string, string | null | undefined]>) => {
    if (!persistFiltersInUrl) return;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      entries.forEach(([key, value]) => {
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      });
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    if (!persistFiltersInUrl) return;
    setKeyword(searchParams.get('q') || '');
    setStatus(searchParams.get('status') || undefined);
    setSeverity(searchParams.get('severity') || undefined);
    setProductKeyword(searchParams.get('product') || '');
    const createdFrom = searchParams.get('created_from');
    const createdTo = searchParams.get('created_to');
    setCreatedRange(createdFrom || createdTo ? [createdFrom ? dayjs(createdFrom) : null, createdTo ? dayjs(createdTo) : null] : null);
    setRoleOnly(searchParams.get('role_only') === '1');
    setPendingOnly(searchParams.get('on_hold') === '1');
    setHasAttachmentsOnly(searchParams.get('has_attachments') === '1');
  }, [persistFiltersInUrl, searchParams]);

  const filteredIncidents = useMemo(() => {
    return incidents.filter((incident) => {
      const roleTasks = (tasksByIncident[incident.id] || []).filter(
        (task) => roles.includes(task.responsible_role)
      );
      const allTasks = tasksByIncident[incident.id] || [];
      const attachmentCount = attachmentsByIncident[incident.id]?.length || 0;
      const matchesRole =
        !roleOnly ||
        roles.includes('ISSUING_PARTY') ||
        roles.some((role) => adminRoles.includes(role)) ||
        roleTasks.some((task) => !['RESOLVED', 'CLOSED'].includes(task.status));
      const matchesKeyword =
        serverKeywordFiltering ||
        !keyword ||
        `${incident.incident_number} ${incident.title} ${incident.phenomenon}`
          .toLowerCase()
          .includes(keyword.toLowerCase());
      const matchesStatus = !status || incident.status === status;
      const matchesSeverity = !severity || incident.severity === severity;
      const matchesProduct =
        !productKeyword ||
        `${incident.model_number || ''} ${incident.lot_number || ''}`
          .toLowerCase()
          .includes(productKeyword.toLowerCase());
      const matchesPending = !pendingOnly || allTasks.some((task) => task.status === 'ON_HOLD');
      const matchesAttachments = !hasAttachmentsOnly || attachmentCount > 0;
      const createdAt = new Date(incident.created_at).getTime();
      const matchesCreatedFrom =
        !createdRange?.[0] || createdAt >= createdRange[0].startOf('day').valueOf();
      const matchesCreatedTo =
        !createdRange?.[1] || createdAt <= createdRange[1].endOf('day').valueOf();
      return (
        matchesKeyword &&
        matchesRole &&
        matchesStatus &&
        matchesSeverity &&
        matchesProduct &&
        matchesPending &&
        matchesAttachments &&
        matchesCreatedFrom &&
        matchesCreatedTo
      );
    });
  }, [attachmentsByIncident, incidents, tasksByIncident, roles, roleOnly, pendingOnly, hasAttachmentsOnly, keyword, status, severity, productKeyword, createdRange, serverKeywordFiltering]);

  const columns: ColumnsType<Incident> = [
    {
      title: t('incident.list.columns.no'),
      dataIndex: 'incident_number',
      width: 130,
    },
    {
      title: t('incident.list.columns.title'),
      dataIndex: 'title',
      align: 'left',
      className: 'incidentTitleColumn',
      render: (value, record) => (
        <Button
          type="link"
          className="tableLink"
          onClick={() => navigate(`/incidents/${record.id}`, { state: { incidentListPath: `${location.pathname}${location.search}` } })}
        >
          {value}
        </Button>
      ),
    },
    {
      title: t('incident.list.columns.status'),
      dataIndex: 'status',
      width: 150,
      render: (value) => <IncidentStatusTag status={value} />,
    },
    {
      title: t('incident.list.columns.severity'),
      dataIndex: 'severity',
      width: 130,
      render: (value) => <IncidentSeverityTag severity={value} />,
    },
    {
      title: t('incident.list.columns.product'),
      dataIndex: 'model_number',
      width: 160,
      render: (value, record) => [value, record.lot_number].filter(Boolean).join(' / ') || '-',
    },
    {
      title: t('incident.list.columns.signals'),
      key: 'signals',
      width: 190,
      render: (_, record) => {
        const tasks = tasksByIncident[record.id] || [];
        const pendingCount = tasks.filter((task) => task.status === 'ON_HOLD').length;
        const openRoleTask = tasks.some(
          (task) => roles.includes(task.responsible_role) && !['RESOLVED', 'CLOSED'].includes(task.status)
        );
        const attachmentCount = attachmentsByIncident[record.id]?.length || 0;
        return (
          <Space size={[4, 4]} wrap>
            {openRoleTask && !roles.includes('ISSUING_PARTY') && (
              <Tag color="blue">{t('incident.list.signals.myTask')}</Tag>
            )}
            {pendingCount > 0 && (
              <Tag color="gold" icon={<ExclamationCircleOutlined />}>
                {t('incident.list.signals.pending', { count: pendingCount })}
              </Tag>
            )}
            {attachmentCount > 0 && (
              <Tag icon={<PaperClipOutlined />}>
                {t('incident.list.signals.attachments', { count: attachmentCount })}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: t('incident.list.columns.updated'),
      dataIndex: 'updated_at',
      width: 180,
      render: (value) => new Date(value).toLocaleString(),
    },
  ];

  return (
    <>
      {showFilters && (
        <Space className="filterBar" wrap>
          {visibleFilterControls.has('keyword') && (
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t('incident.list.filters.keyword')}
              value={keyword}
              onChange={(event) => {
                setKeyword(event.target.value);
                setFilterParam('q', event.target.value.trim() || null);
              }}
            />
          )}
          {fixedContextLabel && <Input disabled value={fixedContextLabel} />}
          {visibleFilterControls.has('status') && (
            <Select
              allowClear
              placeholder={t('incident.list.filters.status')}
              value={status}
              onChange={(value) => {
                setStatus(value);
                setFilterParam('status', value || null);
              }}
              options={statusOptions.map((value) => ({ value, label: t(`incident.status.${value}`) }))}
            />
          )}
          {visibleFilterControls.has('severity') && (
            <Select
              allowClear
              placeholder={t('incident.list.filters.severity')}
              value={severity}
              onChange={(value) => {
                setSeverity(value);
                setFilterParam('severity', value || null);
              }}
              options={['low', 'medium', 'high', 'critical'].map((value) => ({
                value,
                label: t(`incident.severity.${value}`),
              }))}
            />
          )}
          {visibleFilterControls.has('product') && (
            <Input
              allowClear
              placeholder={t('incident.list.filters.productLot')}
              value={productKeyword}
              onChange={(event) => {
                setProductKeyword(event.target.value);
                setFilterParam('product', event.target.value.trim() || null);
              }}
            />
          )}
          {visibleFilterControls.has('createdRange') && (
            <DatePicker.RangePicker
              value={createdRange}
              onChange={(value) => {
                setCreatedRange(value);
                setFilterParams([
                  ['created_from', value?.[0]?.format('YYYY-MM-DD') || null],
                  ['created_to', value?.[1]?.format('YYYY-MM-DD') || null],
                ]);
              }}
              placeholder={[t('incident.list.filters.createdFrom'), t('incident.list.filters.createdTo')]}
            />
          )}
          {showRoleFilters && visibleFilterControls.has('roleOnly') && (
            <Space>
              <Switch
                checked={roleOnly}
                onChange={(checked) => {
                  setRoleOnly(checked);
                  setFilterParam('role_only', checked ? '1' : null);
                }}
                disabled={roles.includes('ISSUING_PARTY') || roles.some((role) => adminRoles.includes(role))}
              />
              <Text>{t('incident.list.filters.roleOnly')}</Text>
            </Space>
          )}
          {visibleFilterControls.has('onHold') && (
            <Space>
              <Switch
                checked={pendingOnly}
                onChange={(checked) => {
                  setPendingOnly(checked);
                  setFilterParam('on_hold', checked ? '1' : null);
                }}
              />
              <Text>{t('incident.list.filters.pendingOnly')}</Text>
            </Space>
          )}
          {visibleFilterControls.has('attachments') && (
            <Space>
              <Switch
                checked={hasAttachmentsOnly}
                onChange={(checked) => {
                  setHasAttachmentsOnly(checked);
                  setFilterParam('has_attachments', checked ? '1' : null);
                }}
              />
              <Text>{t('incident.list.filters.hasAttachments')}</Text>
            </Space>
          )}
        </Space>
      )}
      <Table rowKey="id" columns={columns} dataSource={filteredIncidents} loading={loading} pagination={{ pageSize: 10 }} />
    </>
  );
};

export default IncidentListTable;
